import React, { forwardRef } from "react";
import { twJoin, twMerge } from "tailwind-merge";

type CardVariant = "default" | "inverted";

type CommonProps = { className?: string };
type Props = {
  dataLoc?: string;
  variant?: CardVariant;
  children: React.ReactNode;
  fullWidth?: boolean;
} & Omit<React.ComponentPropsWithoutRef<"div">, "className"> &
  CommonProps;
type HeaderProps = { children: React.ReactNode } & CommonProps;
type TitleProps = { title: string; dataLoc?: string } & CommonProps;
type ContentProps = {
  children: React.ReactNode | React.ReactNode[];
} & CommonProps;

type CardComponent = React.ForwardRefExoticComponent<
  Props & React.RefAttributes<HTMLDivElement>
> & {
  Title: React.FC<TitleProps>;
  Header: React.FC<HeaderProps>;
  Content: React.FC<ContentProps>;
};

export const Card = forwardRef<HTMLDivElement, Props>(
  (
    {
      dataLoc,
      variant = "default",
      className,
      children,
      fullWidth = false,
      ...restOfProps
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={twMerge(
        "rounded-md border px-4 pb-4 pt-3",
        variant === "default" && "border-transparent bg-gray-50",
        variant === "inverted" && "border-gray-200 bg-white",
        fullWidth && "w-full",
        className,
      )}
      data-loc={dataLoc}
      {...restOfProps}
    >
      {children}
    </div>
  ),
) as CardComponent;

Card.Title = ({ className, title, dataLoc }) => (
  <span
    className={twJoin("text-gray-600 font-inter-medium-12px", className)}
    data-loc={dataLoc}
  >
    {title}
  </span>
);

Card.Header = ({ className, children }) => (
  <div
    className={twJoin("flex items-center justify-between space-x-3", className)}
  >
    {children}
  </div>
);

Card.Content = ({ className, children }) => (
  <div className={twJoin("space-y-3", className)}>{children}</div>
);
